import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';

// AI Counsel — the lawyers' opinion-drafting workbench over the Thai statute library.
// Enter the client question → the server routes to relevant chapters and drafts a legal opinion
// citing exact section numbers. The output is a DRAFT for a licensed lawyer to review and sign.
type Act = { id: string; act_key: string; name_en: string; name_th: string; category: string; year_be: string; translation: string; sections: number; version_note: string; source_date: string };

const G = 'var(--accent)';
const CAT_LABEL: Record<string, string> = { common: 'Common', business: 'Business', family: 'Family', property: 'Property' };
const CAT_COLOR: Record<string, string> = { common: '#64748B', business: '#2563EB', family: '#DB2777', property: '#0E7C66' };

export default function AiCounsel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [acts, setActs] = useState<Act[]>([]);
  const [question, setQuestion] = useState('');
  const [lang, setLang] = useState<'en' | 'th' | 'zh'>('en');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [chapters, setChapters] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const me = useStore(s => s.me);
  const [trStatus, setTrStatus] = useState<any>(null);   // English-translation background job
  const trPoll = useRef<any>(null);

  useEffect(() => { api.lawActs().then((d: any) => setActs(d.acts || [])).catch(() => {}); }, []);
  const loadTr = () => api.lawTranslateStatus().then((d: any) => {
    setTrStatus(d);
    if (d?.run?.running) { if (!trPoll.current) trPoll.current = setInterval(loadTr, 5000); }
    else if (trPoll.current) { clearInterval(trPoll.current); trPoll.current = null; api.lawActs().then((x: any) => setActs(x.acts || [])).catch(() => {}); }
  }).catch(() => {});
  useEffect(() => { if (me?.role === 'admin') loadTr(); return () => { if (trPoll.current) clearInterval(trPoll.current); }; }, [me]); // eslint-disable-line
  const startTranslate = async () => {
    if (!window.confirm(t('lc_translate_confirm'))) return;
    try { await api.lawTranslate(); loadTr(); } catch (e: any) { alert(e.message); }
  };

  const generate = async () => {
    if (busy || question.trim().length < 10) return;
    setBusy(true); setErr(''); setDraft(''); setChapters([]);
    try {
      const r: any = await api.lawDraft(question.trim(), lang);
      setDraft(r.draft || ''); setChapters(r.chapters || []);
    } catch (e: any) { setErr(e.message || 'Draft failed'); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(draft).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); };

  const total = acts.reduce((s, a) => s + (a.sections || 0), 0);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_ai_counsel')}</div>
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('lc_lib_stats', { acts: acts.length, sections: total })}</span>
        <div style={{ flex: 1 }} />
        {me?.role === 'admin' && trStatus && (trStatus.run?.running
          ? <span style={{ fontSize: 11.5, color: '#B45309', display: 'inline-flex', alignItems: 'center', gap: 5 }}><i className="ti ti-loader-2" style={{ animation: 'spin 0.8s linear infinite' }} />{t('lc_translating', { done: trStatus.run.done, total: trStatus.run.total })}</span>
          : trStatus.pending > 0
            ? <button onClick={startTranslate} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #B45309', background: 'transparent', color: '#B45309', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}><i className="ti ti-language" style={{ fontSize: 14 }} />{t('lc_translate_pending', { n: trStatus.pending })}</button>
            : <span style={{ fontSize: 11, color: '#15803D' }}><i className="ti ti-circle-check" style={{ marginRight: 3 }} />{t('lc_translate_done')}</span>)}
      </header>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Library sidebar */}
        <div style={{ flex: '0 0 300px', borderRight: '1px solid var(--border)', overflow: 'auto', padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '.04em' }}>{t('lc_library')}</div>
          {acts.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{t('lc_lib_empty')}</div>}
          {acts.map(a => (
            <div key={a.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '9px 11px', marginBottom: 8, background: 'var(--bg-surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: CAT_COLOR[a.category] || '#64748B', borderRadius: 5, padding: '1px 7px' }}>{CAT_LABEL[a.category] || a.category}</span>
                <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{a.sections} §</span>
                {a.translation === 'ai' && <span title={t('lc_ai_translation')} style={{ fontSize: 10, color: '#B45309' }}>EN*</span>}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 }}>{a.name_en}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{a.name_th}{a.year_be ? ` · B.E.${a.year_be}` : ''}</div>
              {a.source_date && <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}><i className="ti ti-clock-hour-4" style={{ fontSize: 10, marginRight: 3 }} />{t('lc_asof', { date: a.source_date })}</div>}
            </div>
          ))}
        </div>

        {/* Workbench */}
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6, letterSpacing: '.04em' }}>{t('lc_question')}</div>
          <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={4} placeholder={t('lc_question_ph')}
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', fontSize: 13.5, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', outline: 'none', resize: 'vertical', lineHeight: 1.6 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <select value={lang} onChange={e => setLang(e.target.value as any)} style={{ padding: '8px 10px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)' }}>
              <option value="en">English</option><option value="th">ไทย</option><option value="zh">中文</option>
            </select>
            <button onClick={generate} disabled={busy || question.trim().length < 10}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 9, border: 'none', background: busy ? 'var(--c-gray-300)' : G, color: '#fff', fontWeight: 700, fontSize: 13, cursor: busy ? 'default' : 'pointer' }}>
              <i className={`ti ti-${busy ? 'loader-2' : 'gavel'}`} style={{ fontSize: 15 }} />{busy ? t('lc_drafting') : t('lc_generate')}</button>
            {busy && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('lc_busy_hint')}</span>}
          </div>

          {err && <div style={{ marginTop: 14, fontSize: 12.5, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 9, padding: '9px 13px' }}>{err}</div>}

          {draft && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '.04em' }}>{t('lc_draft')}</div>
                {chapters.map((c, i) => <span key={i} style={{ fontSize: 10.5, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 5, padding: '1px 7px' }}>{c.act} · {c.path || 'main'} ({c.sections}§)</span>)}
                <div style={{ flex: 1 }} />
                <button onClick={copy} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                  <i className="ti ti-copy" style={{ marginRight: 4 }} />{copied ? t('tg_copied') : t('tg_copy_link')}</button>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.75, color: 'var(--text-primary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 22px' }}>{draft}</div>
              <div style={{ marginTop: 8, fontSize: 11.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '7px 11px' }}>{t('lc_disclaimer')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
